import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { useIsMobile } from '../../hooks/useIsMobile';

// Hook pour persister l'état des cartes pliées
const useCollapsedState = (cardId, defaultCollapsed = false) => {
  const storageKey = `odys_card_${cardId}`;
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return defaultCollapsed;
    const saved = localStorage.getItem(storageKey);
    return saved !== null ? JSON.parse(saved) : defaultCollapsed;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(isCollapsed));
  }, [isCollapsed, storageKey]);

  return [isCollapsed, setIsCollapsed];
};

const CollapsibleCard = ({ 
  id,
  title, 
  icon, 
  children, 
  className = '',
  headerClassName = '',
  defaultCollapsed = false,
  mobileDefaultCollapsed = null,
  headerRight = null,
  compact = false
}) => {
  const isMobile = useIsMobile();
  // Sur mobile, les sections secondaires sont repliées par défaut — état mémorisé séparément
  // pour ne pas écraser le choix fait sur ordinateur.
  const mobileScoped = mobileDefaultCollapsed !== null && isMobile;
  const [isCollapsed, setIsCollapsed] = useCollapsedState(
    mobileScoped ? `${id}_m` : id,
    mobileScoped ? mobileDefaultCollapsed : defaultCollapsed
  );

  return (
    <Card className={`card-base overflow-hidden transition-all duration-200 ${className}`}>
      <CardHeader 
        className={`cursor-pointer select-none transition-all hover:bg-secondary/30 ${compact ? 'py-2 px-3' : 'pb-2'} ${headerClassName}`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className={`flex items-center gap-2 ${compact ? 'text-sm' : 'text-base'}`}>
            {icon}
            {title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {headerRight}
            <motion.div
              animate={{ rotate: isCollapsed ? -90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-muted-foreground`} />
            </motion.div>
          </div>
        </div>
      </CardHeader>
      
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <CardContent className={compact ? 'pt-0 pb-2 px-3' : 'pt-0'}>
              {children}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

export { CollapsibleCard, useCollapsedState };
export default CollapsibleCard;
